import { useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { CheckCircle2, KeyRound, Loader2 } from "lucide-react";
import { api, formatApiError } from "@/lib/api";
import { Logo } from "@/components/common/Logo";
import { TextField } from "@/components/common/Forms";

const Shell = ({ title, text, children, testid }) => (
  <div className="flex min-h-screen items-center justify-center bg-[#F4F6F2] px-6 py-16" data-testid={testid}>
    <div className="card-base w-full max-w-md p-8 sm:p-10">
      <Logo />
      <span className="mt-8 flex h-11 w-11 items-center justify-center rounded-lg bg-[#FBEDEA]"><KeyRound className="h-5 w-5 text-[#C20500]" /></span>
      <h1 className="mt-5 text-3xl text-slate-900">{title}</h1>
      <p className="mt-2 text-sm text-slate-600">{text}</p>
      {children}
      <Link to="/login" className="mt-8 inline-block text-sm text-slate-500 hover:text-slate-900" data-testid="reset-back-login">← Back to sign in</Link>
    </div>
  </div>
);

const Done = ({ text, testid }) => (
  <p className="mt-6 flex items-start gap-2 rounded-md border border-emerald-200 bg-emerald-50 p-3 text-sm text-emerald-800" data-testid={testid}>
    <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" /> {text}
  </p>
);

const ErrorBox = ({ msg, testid }) => msg ? <p className="rounded-md border border-red-200 bg-red-50 p-3 text-sm text-red-700" data-testid={testid}>{msg}</p> : null;

export function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/forgot-password", { email });
      setDone(data.message);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Forgot password?" text="Enter your registered email and we'll send you a secure link to set a new password." testid="forgot-password-page">
      {done ? <Done text={done} testid="forgot-password-success" /> : (
        <form onSubmit={submit} className="mt-6 space-y-5" data-testid="forgot-password-form">
          <TextField id="forgot-email" type="email" label="Email" value={email} onChange={setEmail} required autoComplete="email" />
          <ErrorBox msg={error} testid="forgot-password-error" />
          <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="forgot-password-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Send reset link</button>
        </form>
      )}
    </Shell>
  );
}

export function ResetPassword() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [password, setPassword] = useState("");
  const [confirm, setConfirm] = useState("");
  const [busy, setBusy] = useState(false);
  const [done, setDone] = useState("");
  const [error, setError] = useState("");
  const submit = async (e) => {
    e.preventDefault();
    if (password !== confirm) return setError("Passwords do not match.");
    setBusy(true);
    setError("");
    try {
      const { data } = await api.post("/auth/reset-password", { token, password });
      setDone(data.message);
    } catch (err) {
      setError(formatApiError(err));
    } finally {
      setBusy(false);
    }
  };
  return (
    <Shell title="Set a new password" text="Choose a strong password of at least 8 characters." testid="reset-password-page">
      {!token ? <div className="mt-6"><ErrorBox msg="This reset link is missing its token. Please request a new link." testid="reset-password-error" /><Link to="/forgot-password" className="btn-dark mt-4" data-testid="reset-request-new">Request new link</Link></div>
        : done ? <><Done text={done} testid="reset-password-success" /><Link to="/login" className="btn-primary mt-4 w-full" data-testid="reset-go-login">Sign in</Link></>
        : (
          <form onSubmit={submit} className="mt-6 space-y-5" data-testid="reset-password-form">
            <TextField id="reset-password" type="password" label="New password" value={password} onChange={setPassword} required minLength={8} autoComplete="new-password" />
            <TextField id="reset-confirm" type="password" label="Confirm new password" value={confirm} onChange={setConfirm} required minLength={8} autoComplete="new-password" />
            <ErrorBox msg={error} testid="reset-password-error" />
            <button type="submit" disabled={busy} className="btn-primary w-full" data-testid="reset-password-submit">{busy && <Loader2 className="h-4 w-4 animate-spin" />} Update password</button>
          </form>
        )}
    </Shell>
  );
}
